import type { ColumnDef } from '@tanstack/react-table';

import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { formatDate } from '@/lib/dateFormatter';
import { StoreOwner } from '@/services/storeOwner/storeOwner.types';
import { Link } from '@tanstack/react-router';

export function getColumns(t: (key: string, values?: Record<string, string>) => string): ColumnDef<StoreOwner>[] {
  return [
    {
      accessorKey: 'clubId',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="font-medium truncate max-w-[1.5rem]">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'clubNameEng',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.storeInfo')} />,
      cell: ({ row }) => {
        const clubName = row.original.clubName;
        const clubNameEng = row.original.clubNameEng;
        const clubImage = row.original.clubImage;

        return !clubName ? (
          <div className="text-center">-</div>
        ) : (
          <div className="flex items-center space-x-3 max-w-[20rem] truncate">
            <img src={clubImage?.path} alt={clubImage?.alt ?? ''} className="w-8 h-8 rounded-full object-cover" />
            <div className="truncate">
              <Link
                to="/store-information/premium-stores/$manage-premium-store"
                params={{
                  'manage-premium-store': row.getValue<string>('clubId'),
                }}
                search={{ q: clubNameEng }}
                className="font-medium text-xs text-link underline"
              >
                {clubNameEng}
              </Link>
              <div className="text-foreground-subtle font-medium text-xs">{clubName}</div>
            </div>
          </div>
        );
      },
      meta: {
        label: 'Store Info',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'clubCeoNameEng',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.ceoInfo')} />,
      cell: ({ row }) =>
        row.getValue('clubCeoNameEng') ? (
          <div className="text-foreground text-xs font-medium">{row.getValue('clubCeoNameEng')}</div>
        ) : (
          <div>-</div>
        ),
      meta: {
        label: 'CEO Info',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'emailAddress',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.emailAddress')} />
      ),
      cell: ({ row }) =>
        row.getValue('emailAddress') ? (
          <div className="text-link underline text-xs">{row.getValue('emailAddress')}</div>
        ) : (
          <div>-</div>
        ),
      meta: {
        label: 'Email Address',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'phoneNumber',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.phoneNumber')} />
      ),
      cell: ({ row }) =>
        row.getValue('phoneNumber') ? (
          <div className="text-foreground text-xs font-medium">{row.getValue('phoneNumber')}</div>
        ) : (
          <div>-</div>
        ),
      meta: {
        label: 'Phone Number',
      },
      enableHiding: false,
    },
    {
      id: 'createdDate',
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdDate')} />
      ),
      cell: ({ row }) =>
        row.getValue('createdDate') ? (
          <div className="text-foreground text-xs font-medium whitespace-nowrap">
            {formatDate(row.getValue('createdDate'), 'YYYY/MM/DD')}
          </div>
        ) : (
          <div className="text-center">-</div>
        ),
      meta: {
        label: 'Created Date',
      },
      enableHiding: false,
      enableColumnFilter: true,
    },
    {
      accessorKey: 'updatedDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.updatedDate')} />
      ),
      cell: ({ row }) =>
        row.getValue('updatedDate') ? (
          <div className="text-foreground text-xs font-medium whitespace-nowrap">
            {formatDate(row.getValue('updatedDate'), 'YYYY/MM/DD')}
          </div>
        ) : (
          <div className="text-center">-</div>
        ),
      meta: {
        label: 'Updated Date',
      },
      enableHiding: false,
    },
  ];
}
